import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { queryKeys } from '@/query/keys';

import { adminS3Endpoints, type S3ConfigDto } from './endpoints';

/** Normalised view model — the wire DTO is all-optional; the UI wants non-null. */
export interface S3ConfigViewModel {
  readonly id: string;
  readonly name: string;
  readonly bucketName: string;
  readonly region: string;
  readonly accessKeyId: string;
  readonly hasSecret: boolean;
  readonly endpointUrl: string;
  readonly folder: string;
  readonly isDefault: boolean;
  readonly active: boolean;
}

export function mapS3Config(dto: S3ConfigDto): S3ConfigViewModel {
  return {
    id: dto.id ?? '',
    name: dto.name ?? '',
    bucketName: dto.bucket_name ?? '',
    region: dto.region ?? '',
    accessKeyId: dto.access_key_id ?? '',
    hasSecret: dto.has_secret ?? false,
    endpointUrl: dto.endpoint_url ?? '',
    folder: dto.folder ?? '',
    isDefault: dto.is_default ?? false,
    active: dto.active ?? true,
  };
}

export interface S3ConfigInput {
  readonly name: string;
  readonly bucketName: string;
  readonly region: string;
  readonly accessKeyId: string;
  /** Plaintext; empty on update means "keep the stored secret". */
  readonly secretAccessKey: string;
  readonly endpointUrl: string;
  readonly folder: string;
  readonly isDefault: boolean;
  readonly active: boolean;
}

export interface UpdateS3ConfigInput extends S3ConfigInput {
  readonly id: string;
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toDto(input: S3ConfigInput, id?: string): S3ConfigDto {
  const dto: S3ConfigDto = {
    ...(id !== undefined ? { id } : {}),
    name: input.name.trim(),
    bucket_name: input.bucketName.trim(),
    region: input.region.trim(),
    access_key_id: blankToUndefined(input.accessKeyId) ?? null,
    endpoint_url: blankToUndefined(input.endpointUrl) ?? null,
    folder: blankToUndefined(input.folder) ?? null,
    is_default: input.isDefault,
    active: input.active,
  };
  const secret = blankToUndefined(input.secretAccessKey);
  if (secret !== undefined) {
    dto.secret_access_key = secret;
  }
  return dto;
}

export function useAdminS3Configs(): UseQueryResult<S3ConfigViewModel[]> {
  return useQuery<S3ConfigViewModel[]>({
    queryKey: queryKeys.s3Configs,
    queryFn: async ({ signal }) => {
      const page = await adminS3Endpoints.getS3Configs({ size: 200, signal });
      return page.content.map(mapS3Config);
    },
  });
}

export function useAdminS3ConfigMutations(): {
  readonly create: UseMutationResult<S3ConfigViewModel, Error, S3ConfigInput>;
  readonly update: UseMutationResult<S3ConfigViewModel, Error, UpdateS3ConfigInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.s3Configs }).catch(() => undefined);
  };

  const create = useMutation<S3ConfigViewModel, Error, S3ConfigInput>({
    mutationFn: async (input) => mapS3Config(await adminS3Endpoints.createS3Config(toDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<S3ConfigViewModel, Error, UpdateS3ConfigInput>({
    mutationFn: async ({ id, ...rest }) =>
      mapS3Config(await adminS3Endpoints.updateS3Config(id, toDto(rest, id))),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (id) => adminS3Endpoints.deleteS3Config(id),
    onSettled: invalidate,
  });

  return { create, update, remove };
}
